import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { ButtonBase, CircularProgress, Modal, Skeleton, Stack, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import api from '../../utils/api';
import MainCarePlan from '../../components/main/MainCarePlan';
import MainCareSports from '../../components/main/MainCareSports';
import DigitalTwinScreen4 from './DigitalTwinScreen4';
import {
  Box,
  DigitalTwinShell,
  DiseaseCards,
  DiseaseRisk,
  ORGAN_MODEL_MAP,
  OrganModelViewer,
  SectionCard,
  SemicircleGauge,
  YearSlider,
  buildDiseaseCards,
} from './DigitalTwinShared';
import {
  findOrganRisk,
  organGradeLabel,
  toRiskSummary,
  useDigitalTwin,
} from './digitalTwinApi';

/**
 * 화면 2: 장기 상세
 * 화면 1 → 좌측 장기 버튼(뇌/심장/신장/간/대장/췌장) 클릭 시 진입
 * URL 쿼리 ?organ=heart 로 어떤 장기인지 전달받아 해당 3D 모델 표시
 * 게이지 카드 우측 상단 "상세분석 >" → 화면 3 으로 이동
 */
// 연도 대신 시점 라벨 — 0:3개월전, 1:현재, 2:5년 후, 3:10년 후
const YEAR_STOPS = [0, 1, 2, 3];
const YEAR_LABELS = ['3개월전', '현재', '5년 후', '10년 후'];

/** 건강 뉴스 목록 — 홈(MainNews)·전체 메뉴(MoreList)의 '전체보기'와 같은 경로 */
const HEALTH_NEWS_PATH = '/News/HealthNews';

/**
 * 그리드 안 카드 버튼 공통 모양.
 *
 * display 를 block 이 아니라 flex(column) 로 두는 이유:
 * ButtonBase 는 <button> 이라, 내용이 버튼보다 짧으면 브라우저가 내용을 세로
 * 가운데로 밀어 놓는다. 그리드 카드들은 같은 높이로 늘어나므로 내용이 짧은
 * 카드만 아래로 내려앉아 썸네일·글자 위치가 옆 카드와 어긋난다.
 */
const gridCardSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'flex-start',
  width: '100%',
  textAlign: 'left',
  borderRadius: '12px',
  overflow: 'hidden',
  bgcolor: '#fff',
  boxShadow: '0 2px 6px rgba(197,211,218,.45)',
};

/** content/readAllCardNews 응답 항목 — 홈(MainNews)·건강 뉴스 목록이 쓰는 것과 같은 형태 */
type CardNews = {
  title: string;
  thumbNail: string;
  images: string[];
  hashTags: string[];
};

/** 이 화면에 띄우는 카드뉴스 수 — 2열 그리드 한 줄 */
const NEWS_COUNT = 2;

/**
 * 카드뉴스 이미지 URL. 홈(MainNews)·건강 뉴스 목록(HealthNews)과 같은 규칙이다.
 * 빈 값이면 undefined 를 돌려준다 — src="" 로 두면 브라우저가 현재 페이지를
 * 이미지로 다시 요청한다(IMG localhost:3000/).
 */
const cardNewsImage = (name?: string): string | undefined => {
  if (!name) return undefined;
  return name.includes('aws') ? name : `https://file.dualhealth.kr/healthContent/cardNews/${name}`;
};

/** 카드뉴스 이미지 뷰어 슬라이더 — 홈(MainNews)과 같은 설정 */
const NEWS_SLIDER_SETTINGS = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
};

/** 이미지 뷰어 모달 박스 — 홈(MainNews)의 style 과 같다 (이 화면 폭 430 에 맞춤) */
const newsModalSx = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'calc(100% - 20px)',
  maxWidth: 430,
  bgcolor: 'background.paper',
  boxShadow: 24,
  '& div :focus-visible': { outline: 0 },
  '& .slick-prev': { left: 0, zIndex: 99 },
  '& .slick-next': { right: 10, zIndex: 99 },
  '& .slick-prev:before, .slick-next:before': { fontSize: 30, color: '#999' },
};

const DigitalTwinScreen2 = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [year, setYear] = useState(1); // '현재'
  // 질환 카드로 연 화면 4 시트 — 화면 1(Modeling)의 drawerDisease 와 같은 역할
  const [drawerDisease, setDrawerDisease] = useState<DiseaseRisk | null>(null);

  // 화면 1 과 같은 AP_009_002 결과를 모듈 캐시로 공유한다 (중복 호출 없음)
  const { data: twin } = useDigitalTwin();
  const diseaseCards = useMemo(() => buildDiseaseCards(twin), [twin]);

  const organKey = searchParams.get('organ') ?? 'heart';
  const organ = ORGAN_MODEL_MAP[organKey] ?? ORGAN_MODEL_MAP.heart;

  // 이 장기의 AP_009_002 판단 결과 (없으면 '미측정'으로 표시)
  const organRisk = useMemo(() => findOrganRisk(twin, organKey), [twin, organKey]);
  const organSummary = useMemo(() => toRiskSummary(organRisk), [organRisk]);
  const organStatus = organGradeLabel(organRisk);

  // 시점 진행에 따른 악화 정도 (현재:0 → 10년 후:1) — 모델 색상·질감 변화
  const decay = Math.max(0, Math.min(1, (year - 1) / 2));

  /*
   * 건강뉴스 — 홈(MainNews)이 쓰는 content/readAllCardNews 를 그대로 쓴다.
   *
   * 홈은 전체 페이지 수만큼(count/10) 요청을 반복해 받은 뒤 매번 섞지만,
   * 여기는 2장만 보여주므로 첫 페이지 한 번만 받는다 — 이 화면은 이미 3D 모델을
   * 내려받는 중이라 요청을 여러 개 얹을 이유가 없다.
   */
  const [news, setNews] = useState<CardNews[] | null>(null);
  const [newsError, setNewsError] = useState(false);
  const [newsNonce, setNewsNonce] = useState(0); // '다시 시도' 트리거
  /** 카드 탭으로 연 이미지 뷰어. null 이면 닫힘 (홈과 같은 동작) */
  const [newsImages, setNewsImages] = useState<string[] | null>(null);

  useEffect(() => {
    let alive = true;
    setNews(null);
    setNewsError(false);
    api
      .post('content/readAllCardNews', { page: 0 })
      .then((res) => {
        if (!alive) return;
        const list = res.data?.cardNewsList;
        setNews(Array.isArray(list) ? list : []);
      })
      .catch((error) => {
        if (!alive) return;
        console.log(error);
        setNewsError(true);
      });
    return () => {
      alive = false;
    };
  }, [newsNonce]);

  // 홈·건강 뉴스 목록과 같이 제목 없는 항목은 거른다
  const newsCards = useMemo(
    () => (news ?? []).filter((item) => !!item?.title).slice(0, NEWS_COUNT),
    [news],
  );

  return (
    <>
      <DigitalTwinShell>
        {/* 장기 3D 모델 (organ 쿼리에 따라 GLB 교체) */}
        <Box sx={{ mx: 2, mt: 2, position: 'relative', height: 300 }}>
          <Suspense
            fallback={
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={28} sx={{ color: '#8aa2ad' }} />
              </Box>
            }
          >
            <OrganModelViewer
              key={organ.path}
              path={organ.path}
              decay={decay}
              organKey={organKey}
            />
          </Suspense>
          {organ.temp && (
            <Typography
              sx={{
                position: 'absolute',
                bottom: 4,
                left: 0,
                right: 0,
                textAlign: 'center',
                fontSize: 11,
                color: '#9aa6b2',
              }}
            >
              ※ {organ.label} 모델 준비 중 (임시 모델 표시)
            </Typography>
          )}
        </Box>

        {/* 시점 슬라이더 */}
        <Box sx={{ mx: 3, mt: 1, mb: 3 }}>
          <YearSlider years={YEAR_STOPS} labels={YEAR_LABELS} value={year} onChange={setYear} />
        </Box>

        {/* 반원 게이지 카드 */}
        <SectionCard sx={{ bgcolor: '#eef2f6' }}>
          <Stack direction="row" justifyContent="flex-end">
            <ButtonBase
              onClick={() => navigate(`/digitalTwin/screen3?organ=${organKey}`)}
              sx={{ color: '#888', fontSize: 13, fontWeight: 600, borderRadius: '8px', px: 0.5 }}
            >
              상세분석 ›
            </ButtonBase>
          </Stack>

          {/* 게이지 값 = AP_009_002 의 장기 score(0~100), 문구 = riskGrade 의 Grade 숫자 */}
          <SemicircleGauge value={organSummary.score ?? 0} status={organStatus} size={258} />

          <Box
            sx={{
              mt: 3,
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 1,
            }}
          >
            {/*
              AP_009_002 가 실제로 주는 장기 지표 3종.
              이전에는 '4년 내 발병율 0.0123% / 3위 / 2배 높음' 이 하드코딩돼 있었는데,
              스펙(Notion APP-DT)에 대응 필드가 없어 계정과 무관하게 같은 값이 나왔다.
              건강 수치를 실제인 것처럼 보이게 두지 않고, 서버가 주는 값으로 교체한다.
            */}
            {[
              {
                label: '신뢰도',
                value: organSummary.confidence ?? '미측정',
                tone: '#2A4268',
              },
              {
                label: '데이터 충족률',
                value: organSummary.coverage === null ? '미측정' : `${organSummary.coverage}%`,
                tone: '#54a4cb',
              },
              {
                label: '위험 점수',
                value: organSummary.score === null ? '미측정' : `${organSummary.score}점`,
                tone: organStatus === '나쁨' ? '#ed5454' : organStatus === '주의' ? '#ff8000' : '#38c172',
              },
            ].map((item) => (
              <Box
                key={item.label}
                sx={{
                  bgcolor: '#f3f6fa',
                  borderRadius: '10px',
                  p: 1.4,
                  minHeight: 96,
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                {/* 라벨 — 왼쪽 위, 줄바꿈은 단어 단위로 */}
                <Typography
                  sx={{ color: '#777', fontSize: 14, lineHeight: 1.3, textAlign: 'left', wordBreak: 'keep-all' }}
                >
                  {item.label}
                </Typography>
                {/* 수치 — 박스 우측 하단 */}
                <Typography sx={{ color: item.tone, fontSize: 14, fontWeight: 900, textAlign: 'right' }}>
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </SectionCard>

        {/* 4개 질환 카드 — 화면 1과 동일하게 누르면 화면 4(질환별 변화 분석) 시트가 열린다.
            카드 데이터(buildDiseaseCards)도 화면 1과 같은 AP_009_002 결과에서 온다. */}
        <Box sx={{ mt: 2 }}>
          <DiseaseCards diseases={diseaseCards} onSelect={setDrawerDisease} />
        </Box>

        {/* 맞춤운동 — 케어 > 맞춤운동(CareSports)과 같은 AP_004_002 값.
            하드코딩이던 근력 운동/자전거/수영 3종을 스트레칭·근력운동·유산소로 바꿨다. */}
        <SectionCard>
          <MainCareSports />
        </SectionCard>

        {/* 맞춤플랜 — 홈 화면과 같은 컴포넌트(로그인/플랜 유무 분기, 이번주 맞춤식단·맞춤운동
            동일). compact 로 아래 건강뉴스와 같은 2열 90px 카드 크기를 쓴다.
            화면 셸에 좌우 여백이 없어 여기서 준다. */}
        <Box sx={{ mx: 2 }}>
          <MainCarePlan variant="compact" />
        </Box>

        {/* 건강뉴스 — 홈(MainNews)과 같은 카드뉴스(content/readAllCardNews).
            카드를 누르면 홈과 동일하게 이미지 뷰어가 열리고, 전체보기는 목록 화면으로 간다. */}
        <Box sx={{ mx: 2, mt: 3 }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 1.2 }}>
            <Typography sx={{ color: '#333', fontSize: 15, fontWeight: 800 }}>건강뉴스</Typography>
            <ButtonBase
              onClick={() => navigate(HEALTH_NEWS_PATH)}
              sx={{ ml: 'auto', color: '#888', fontSize: 13, fontWeight: 600, borderRadius: '8px', px: 0.5 }}
            >
              전체보기 ›
            </ButtonBase>
          </Stack>

          {/* 조회 중 */}
          {news === null && !newsError && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.2 }}>
              {[0, 1].map((slot) => (
                <Box key={slot} sx={{ borderRadius: '12px', overflow: 'hidden', bgcolor: '#fff' }}>
                  <Skeleton variant="rectangular" height={90} />
                  <Box sx={{ p: 1 }}>
                    <Skeleton height={16} />
                    <Skeleton height={14} width="60%" />
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {/* 조회 실패 — 예시 뉴스를 대신 보여주지 않고 상태를 그대로 알린다 (화면 1과 같은 원칙) */}
          {newsError && (
            <Box
              sx={{
                px: 1.6,
                py: 1.2,
                borderRadius: '12px',
                bgcolor: '#fdeaea',
                display: 'flex',
                alignItems: 'center',
                gap: 1.2,
              }}
            >
              <Typography sx={{ flex: 1, fontSize: 12.5, lineHeight: 1.45, color: '#c2494e' }}>
                건강뉴스를 불러오지 못했습니다.
              </Typography>
              <ButtonBase
                onClick={() => setNewsNonce((n) => n + 1)}
                sx={{
                  flexShrink: 0,
                  px: 1.4,
                  height: 28,
                  borderRadius: '99px',
                  bgcolor: '#fff',
                  color: '#c2494e',
                  fontSize: 12.5,
                  fontWeight: 800,
                }}
              >
                다시 시도
              </ButtonBase>
            </Box>
          )}

          {news !== null && !newsError && newsCards.length === 0 && (
            <Typography sx={{ fontSize: 12.5, color: '#9aa6b2', py: 1 }}>
              등록된 건강뉴스가 없습니다.
            </Typography>
          )}

          {newsCards.length > 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.2 }}>
              {newsCards.map((item, index) => (
                <ButtonBase
                  key={`${item.title}-${index}`}
                  onClick={() => {
                    // 이미지가 없는 항목은 뷰어에 띄울 게 없으므로 목록 화면으로 보낸다
                    const images = item.images ?? [];
                    if (images.length === 0) navigate(HEALTH_NEWS_PATH);
                    else setNewsImages(images);
                  }}
                  sx={gridCardSx}
                >
                  <Box sx={{ height: 90, flexShrink: 0, bgcolor: '#eef2f6', overflow: 'hidden' }}>
                    <Box
                      component="img"
                      src={cardNewsImage(item.thumbNail)}
                      alt={item.title}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  </Box>
                  <Box sx={{ p: 1 }}>
                    {/* 제목 길이가 항목마다 달라 2줄로 자른다 (건강 뉴스 목록과 같은 처리).
                        minHeight 로 2줄을 확보해 제목이 1줄인 카드와 태그 줄 위치를 맞춘다. */}
                    <Typography
                      sx={{
                        color: '#333',
                        fontSize: 12,
                        fontWeight: 700,
                        mb: 0.6,
                        lineHeight: 1.3,
                        minHeight: '2.6em',
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: '2',
                        overflow: 'hidden',
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {(item.hashTags ?? []).map((tag, tagIndex) => (
                        <Box
                          key={`${tag}-${tagIndex}`}
                          sx={{
                            fontSize: 10,
                            color: '#777',
                            bgcolor: '#f3f6fa',
                            borderRadius: '99px',
                            px: 0.8,
                            py: 0.2,
                          }}
                        >
                          {tag}
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </ButtonBase>
              ))}
            </Box>
          )}

          {/* 출처 — 홈·건강 뉴스 목록과 같은 표기 */}
          <Typography sx={{ mt: 0.8, textAlign: 'right', fontSize: 11, color: '#aaa' }}>
            출처 : 듀얼헬스케어,
            <Box
              component="a"
              href="https://blog.naver.com/dualhealth_official"
              title="듀얼헬스케어"
              target="_blank"
              rel="noreferrer"
              sx={{ color: '#aaa', textDecoration: 'none' }}
            >
              {' '}
              https://blog.naver.com/dualhealth_official
            </Box>
          </Typography>
        </Box>
      </DigitalTwinShell>

      {/* 질환 카드 → 질환별 변화 분석 시트 (화면 1과 동일한 화면 4를 그대로 쓴다).
          시트는 position:fixed 라 화면 셸(Container) 밖에 둔다 — 화면 1도 같은 배치다. */}
      <DigitalTwinScreen4
        open={drawerDisease !== null}
        disease={drawerDisease}
        onClose={() => setDrawerDisease(null)}
      />

      {/* 카드뉴스 이미지 뷰어 — 홈(MainNews)·건강 뉴스 목록과 같은 슬라이더 모달 */}
      <Modal open={newsImages !== null} onClose={() => setNewsImages(null)}>
        <Box sx={newsModalSx}>
          <Slider {...NEWS_SLIDER_SETTINGS}>
            {(newsImages ?? []).map((image, index) => (
              <Box key={`${image}-${index}`} sx={{ '&:focus-visible': { outline: 0 } }}>
                <Box
                  component="img"
                  alt=""
                  src={cardNewsImage(image)}
                  sx={{ m: 'auto', display: 'block', maxWidth: '100%', maxHeight: '100%' }}
                />
              </Box>
            ))}
          </Slider>
        </Box>
      </Modal>
    </>
  );
};

export default DigitalTwinScreen2;
